/* eslint-disable import/no-cycle */

import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';
import { DirectSalesAllocationSolutionStatus } from 'components/common/types/DirectSalesAllocationSolution';
import { DirectSalesTargetTolerance } from 'components/common/types/DirectSalesTargetTolerance.types';
import { SWEEP_OPTIONS_INFO, SWEEP_OPTIONS_NAMES } from 'consts/deal';
import { CampaignVersioningEditingStatus, CampaignVersioningErrors } from 'consts/directSalesCampaignVersioning';
import { DirectSalesNegotiationTypeConfig } from './DirectSalesNegotiationTypeConfig';
import { PatternOption } from '../DaypartTargeting/DaypartTargeting.types';
import { CodeNameModel, FileList } from '.';
import {
  Advertiser,
  Brand,
  CommonDeal,
  FilteredCampaignsRequestResponse,
  GroupOfSelectedDays,
  Line,
  RouteFrameCode,
  SweepOptions,
  TagInclude,
} from './Deal.types';
import { DirectSalesExclusionFlag, DirectSalesExclusions } from './DirectSalesDeal.types';
import { CodeName } from './CodeName.types';
import { Asset } from './Asset.types';
import { PlannerPoi, POI } from './Planner.types';
import { SweepLabelDescriptionOptions } from '../../pages/Planner/PlannerSections/ShareOfTime/ShareOfTimePanel/ShareOfTimePanel.types';

export enum DirectSalesSweep {
  NONE = 'NONE',
  SWEEP_TO_FILL = 'DIRTY',
  SWEEP_TO_HOUR = 'HOUR',
  SWEEP_TO_DAY = 'DAY',
  SWEEP_WEIGHTED = 'WEIGHTED',
}

export const DIRECT_SALES_SWEEP: SweepLabelDescriptionOptions = {
  [DirectSalesSweep.NONE]: {
    label: SWEEP_OPTIONS_NAMES[SweepOptions.NONE],
    description: SWEEP_OPTIONS_INFO[SweepOptions.NONE],
  },
  [DirectSalesSweep.SWEEP_TO_FILL]: {
    label: SWEEP_OPTIONS_NAMES[SweepOptions.SWEEP_TO_FILL],
    description: SWEEP_OPTIONS_INFO[SweepOptions.SWEEP_TO_FILL],
  },
  [DirectSalesSweep.SWEEP_TO_HOUR]: {
    label: SWEEP_OPTIONS_NAMES[SweepOptions.SWEEP_TO_HOUR],
    description: SWEEP_OPTIONS_INFO[SweepOptions.SWEEP_TO_HOUR],
  },
  [DirectSalesSweep.SWEEP_TO_DAY]: {
    label: SWEEP_OPTIONS_NAMES[SweepOptions.SWEEP_TO_DAY],
    description: SWEEP_OPTIONS_INFO[SweepOptions.SWEEP_TO_DAY],
  },
  [DirectSalesSweep.SWEEP_WEIGHTED]: {
    label: SWEEP_OPTIONS_NAMES[SweepOptions.SWEEP_WEIGHTED_DISTRIBUTION],
    description: SWEEP_OPTIONS_INFO[SweepOptions.SWEEP_WEIGHTED_DISTRIBUTION],
  },
};

export enum DirectSalesCampaignType {
  STANDARD = 'STANDARD',
}

export enum DirectSalesFilterRelationship {
  AND = 'AND',
  OR = 'OR',
}

export enum DirectSalesSubOptionTypes {
  LOCATION = 'location',
  FORMAT = 'format',
}

export enum DirectSalesFilterGroupCriteria {
  TAG = 'TAG',
  VOLUME_TARGET = 'VOLUME_TARGET',
  BUDGET_TARGET = 'BUDGET_TARGET',
  FRAME_TARGET = 'FRAME_TARGET',
  SOT_TARGET = 'SOT_TARGET',
  FRAME_LIST = 'FRAME_LIST',
  NETWORK_TARGET = 'NETWORK_TARGET',
}

export enum DirectSalesFilterScope {
  INCLUDE = 'INCLUDE',
  EXCLUDE = 'EXCLUDE',
}

export enum DirectSalesMediaTypeLabel {
  DIGITAL = 'Digital',
  PAPER = 'Paper',
}

export enum DirectSalesMediaType {
  DIGITAL = 'DIGITAL',
  PAPER = 'PAPER',
}

export type DirectSalesTargetTolerancePriority = DirectSalesTargetTolerance & {
  priority: CodeNameModel;
  objectiveId?: number | null;
};

export enum DirectSalesPricingMode {
  CPM = 'CPM',
  PANEL = 'PANEL',
  NETWORK = 'NETWORK',
}

export const DIRECT_SALES_PRICING_MODES: Record<DirectSalesPricingMode, string> = {
  [DirectSalesPricingMode.CPM]: 'CPM',
  [DirectSalesPricingMode.PANEL]: 'Panel',
  [DirectSalesPricingMode.NETWORK]: 'Network',
};

export enum DirectSalesProductCategoryExclusivityType {
  OFF = 'OFF',
  FRAME = 'FRAME',
  FURNITURE = 'FURNITURE',
}

export enum DirectSalesProductCategoryExclusivityLabel {
  OFF = 'None',
  FRAME = 'Frame',
  FURNITURE = 'Furniture',
}

export type DirectSalesAsset = Pick<Asset, 'longitude' | 'latitude' | 'frameId'>;

export interface DirectSalesAssetLine {
  framesCount: number;
  impressionsCount: number;
  assets: DirectSalesAsset[];
  missingAssets: DirectSalesAsset[];
}

export type DateSelectionType = 'FIXED' | 'FIXED_AND_FLOATING';

export type PeriodType = 'SPAN' | 'DAY' | 'WEEK';

export type Days = 'SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY';

export type ProductTemplate = { id: string; name: string };

export type DirectSales = {
  dealLineFormats: DirectSalesCodeNameModel[];
  locations: DirectSalesCodeNameModel[];
  poi: POI[];
  poiPoints?: PlannerPoi[];
  transientState?: CampaignVersioningEditingStatus | CampaignVersioningErrors;
  mediaType?: DirectSalesMediaType;
  budgetCriteria: DirectSalesTargetTolerancePriority;
  framesCriteria: DirectSalesTargetTolerancePriority;
  impressionsCriteria: DirectSalesTargetTolerancePriority;
  networkCriteria: NetworkCriteriaType[];
  state: DirectSalesState;
  lockState: DirectSalesLockState;
  pricingMode?: DirectSalesPricingMode;
  allocation: DirectSalesLineAllocation;
  objectives: DirectSalesLineObjective[];
  sessionLockState?: DirectSalesLockState;
  transientLineStates?: DirectSalesTransientDealLineState;
  productTemplate: ProductTemplate;
  floatingRange: FloatingDateRange;
  sotObjectiveId?: number | null;
  enabledForSharing?: boolean;
  campaignPlanShareUrl?: string;
  campaignPlanShareExpiresAt?: string;
  isGenerateCampaignPlanEnabled?: boolean;
  hasUnsavedCampaignPlanConfigChanges?: boolean;
  isCampaignPlanLinkDiscardModalOpen?: boolean;
  updatedGridNegotiationTypes: DirectSalesUpdatedCampaignNegotiationTypes;
  failedGridNegotiationTypes?: string[];
  periodType?: PeriodType;
  weekStartDate?: Days | null;
  oocReport?: OOCReport;
  allocationFailures?: DirectSalesDealLineAllocationFailureResponse[];
};

export type DirectSalesLine = Line & DirectSales;

export interface DirectSalesCampaign extends CommonDeal, DirectSales {
  sessionState: DirectSalesLockState;
}

export interface DirectSalesCodeNameModel extends Pick<CodeNameModel, 'code' | 'name' | 'include' | 'mediaOwner'> {
  status?: DirectSalesFilterScope;
  category?: string;
}

export interface TagCategory extends DirectSalesCodeNameModel {
  tags: TagInclude[];
}

export type DirectSalesSubOptions = {
  [k in DirectSalesSubOptionTypes]?: CodeNameModel[];
};

export interface DirectSalesOption extends DirectSalesSubOptions {
  code: string;
  name: string;
}

export type DirectSalesCampaignHeader = {
  id: string;
  userVisibleId?: string | null;
  publisher: string;
  version: string;
  sessionLockStatus: DirectSalesLockState;
  salesPerson?: CodeNameModel;
  salesTeam?: CodeNameModel;
  adminPerson?: CodeNameModel;
  enabledForSharing?: boolean;
  error?: string;
};

export type DirectSalesCampaignDealLevelDefinition = {
  campaignGroup: DirectSalesCampaignType;
  campaignType: DirectSalesCampaignType;
  advertiserId: string;
  brandId: string;
  productCategory: string;
  agencyId: string;
  specialistId: string;
  salesPerson?: CodeNameModel | null;
  salesTeam?: CodeNameModel | null;
  adminPerson?: CodeNameModel | null;
  name?: string;
  productCategoryExclusivity?: DirectSalesProductCategoryExclusivityType;
  juxtapositionExclusion: DirectSalesExclusionFlag;
  specialistBlockExclusion: DirectSalesExclusionFlag;
  advertiserBlockExclusion: DirectSalesExclusionFlag;
  agencyBlockExclusion: DirectSalesExclusionFlag;
  duplicationExclusion: DirectSalesExclusionFlag;
};

export type DirectSalesExclusionsRequest = {
  exclusions: { organisations: string[]; productCategories: string[] };
};

type DirectSalesCampaignDefinitionEnrichmentResponse = {
  status: DirectSalesStatusOption;
  advertiserName: string;
  brandName: string;
  productCategoryName: string;
  agencyName?: string;
  specialistName?: string;
  name?: string;
};

export type DirectSalesCampaignDefinitionRequest = DirectSalesCampaignDealLevelDefinition &
  DirectSalesCampaignMultiDealLineRequest &
  DirectSalesExclusionsRequest & {
    duplicationExclusion?: DirectSalesExclusionFlag;
    juxtapositionExclusion: DirectSalesExclusionFlag;
  };

export type DirectSalesCampaignDefinitionResponse = Omit<DirectSalesCampaignDefinitionRequest, 'dealLines'> &
  DirectSalesCampaignDefinitionEnrichmentResponse & {
    dealLines: DirectSalesCampaignDealLineResponse[];
    exclusions: DirectSalesExclusions;
  };

export type DirectSalesCampaignDefinitionExclusions = DirectSalesCampaignDealLevelDefinition & {
  exclusions: DirectSalesExclusions;
};

export type DirectSalesAllocationSolutionResponse = {
  allocationSolutionId: string;
  lastValidAllocationSolutionId?: string | null;
  acceptTransient?: boolean;
  createdAt: number;
  measures: {
    campaignMeasures: DirectSalesCampaignMeasure[];
    nbGreenObjectives: number;
    nbOrangeObjectives: number;
    nbRedObjectives: number;
  };
  errors: string[] | null;
};

export type DirectSalesAllocationMeasure = {
  allocated: number;
  status: DirectSalesAllocationSolutionStatus;
};

export type DirectSalesLineAllocation = Record<number, DirectSalesAllocationMeasure>;

export type DirectSalesTargetAllocation = {
  objectiveId: number | null;
  status?: DirectSalesAllocationSolutionStatus;
  value?: number;
};

export type DirectSalesObjectiveTarget = DirectSalesTargetTolerancePriority & {
  value?: number;
  objectiveId?: number | null;
};

export type DirectSalesObjectiveFilterType = 'locations' | 'formats' | 'poi' | 'tags' | 'frameList';

export type DirectSalesObjectiveFrameListFilter = {
  listFiles: FileList[];
  routeFrameCodes: RouteFrameCode[];
  mapRouteFrameCodes?: RouteFrameCode[];
};

export type DirectSalesObjectiveToRemove = CodeName & {
  lineId?: string;
};

export type DirectSalesLineObjective = {
  objectiveGroupId: number;
  objectiveStatus?: DirectSalesAllocationSolutionStatus;
  name: string;
  formats: DirectSalesCodeNameModel[];
  locations: DirectSalesCodeNameModel[];
  poi: POI[];
  tags: CodeNameModel[];
  frameList: DirectSalesObjectiveFrameListFilter;
  budget: DirectSalesObjectiveTarget;
  frames: DirectSalesObjectiveTarget;
  impressions: DirectSalesObjectiveTarget;
};

type DirectSalesPricingSolutionTotals = {
  [key: string]: string;
};

export type DirectSalesPricingSolutionResponse = {
  allocationSolutionUUID: string;
  pricingSetVersion: string;
  totalCampaignImpressions: string;
  totalCampaignPrice: string;
  dealLineTotals: DirectSalesPricingSolutionTotals;
  dealLineTotalImpressions: DirectSalesPricingSolutionTotals;
  pricingTypeTotals: DirectSalesPricingSolutionTotals;
  pricingTypeTotalImpressions: DirectSalesPricingSolutionTotals;
  networkTotals?: number | null;
  panelPriceTotals?: number | null;
  grid: DirectSalesGrid;
};

export type DirectSalesCampaignPricing = {
  grid: DirectSalesGrid;
  gridNegotiation: DirectSalesNegotiationTypeConfig;
};

type DirectSalesAudienceMeasure = {
  contacts: number;
  key: string;
};

type DirectSalesCampaignMeasure = {
  campaignId: string;
  bookingMeasures: DirectSalesBookingMeasure[];
};

type DirectSalesBookingMeasure = {
  bookingId: string;
  objectiveMeasures: DirectSalesBookingObjectiveMeasure[];
  audienceMeasures: DirectSalesAudienceMeasure[];
  nbUnits: number;
  status: DirectSalesAllocationSolutionStatus;
};

type DirectSalesBookingObjectiveMeasure = {
  objectiveId: string;
  type: string;
  value: number;
  status: DirectSalesAllocationSolutionStatus;
  gapToTarget: number;
};

export type FloatingDateRange = {
  floatingStartDate: string | Date;
  floatingEndDate: string | Date;
} | null;

type DirectSalesCampaignDealLineRangeRequest = {
  startDate: string | null;
  endDate: string | null;
  floatingRange: FloatingDateRange;
};

export interface DirectSalesRequestFormatCriteria extends DirectSalesTargetTolerance {
  target: number;
  priority?: number;
  boostedTarget?: boolean;
  weight?: number;
  networkId?: string;
  networkName?: string;
  relative?: boolean;
}

export type DirectSalesTargetCriteria = {
  objectiveId: number | null;
  value: DirectSalesRequestFormatCriteria;
  filter?: DirectSalesFilter;
  criteriaType: DirectSalesFilterGroupCriteria;
  objectiveStatus?: DirectSalesAllocationSolutionStatus;
  objectiveGroupId?: number;
  name?: string;
  type?: string;
};

export enum DirectSalesStatusOption {
  DRAFT_UPDATING = 'DRAFT_UPDATING',
  DRAFT_SOLVED = 'DRAFT_SOLVED',
  DRAFT_FAILED_SOLVE = 'DRAFT_FAILED_SOLVE',
  DRAFT_PRICED = 'DRAFT_PRICED',
  DRAFT_FAILED_PRICE = 'DRAFT_FAILED_PRICE',
  DRAFT_SOLVING = 'DRAFT_SOLVING',
  DRAFT_PROPOSAL_SOLVING = 'DRAFT_PROPOSAL_SOLVING',
  DRAFT_SOLVING_OPTION_RESERVATION = 'DRAFT_SOLVING_OPTION_RESERVATION',
  DRAFT_SOLVING_CONFIRMED_RESERVATION = 'DRAFT_SOLVING_CONFIRMED_RESERVATION',
  DRAFT_PRICING_OPTION_RESERVATION = 'DRAFT_PRICING_OPTION_RESERVATION',
  DRAFT_PRICING_CONFIRMED_RESERVATION = 'DRAFT_PRICING_CONFIRMED_RESERVATION',
  DRAFT_PROPOSAL_PRICING = 'DRAFT_PROPOSAL_PRICING',
  PROPOSAL_SOLVING = 'PROPOSAL_SOLVING',
  PROPOSAL_PROPOSAL_PRICING = 'PROPOSAL_PROPOSAL_PRICING',
  PROPOSAL_PRICING_OPTION_RESERVATION = 'PROPOSAL_PRICING_OPTION_RESERVATION',
  PROPOSAL_PRICING_CONFIRMED_RESERVATION = 'PROPOSAL_PRICING_CONFIRMED_RESERVATION',
  PROPOSAL_SOLVING_OPTION_RESERVATION = 'PROPOSAL_SOLVING_OPTION_RESERVATION',
  PROPOSAL_SOLVING_CONFIRMED_RESERVATION = 'PROPOSAL_SOLVING_CONFIRMED_RESERVATION',
  DRAFT = 'DRAFT',
  OPTION = 'OPTION',
  PROPOSAL = 'PROPOSAL',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  DELETED = 'DELETED',
  MIXED = 'MIXED',
  LIVE = 'LIVE',
  ENDED = 'ENDED',
}

export type DirectSalesFailedStatus =
  | DirectSalesStatusOption.DRAFT_FAILED_PRICE
  | DirectSalesStatusOption.DRAFT_FAILED_SOLVE;

export const DIRECT_SALES_FAILED_STATUSES: DirectSalesStatusOption[] = [
  DirectSalesStatusOption.DRAFT_FAILED_PRICE,
  DirectSalesStatusOption.DRAFT_FAILED_SOLVE,
];

export const DirectSalesStatusOptionEvent: Record<string, string> = {
  [DirectSalesStatusOption.OPTION]: 'TO_OPTION',
  [DirectSalesStatusOption.PROPOSAL]: 'TO_PROPOSAL',
  [DirectSalesStatusOption.CONFIRMED]: 'CONFIRM',
  [DirectSalesStatusOption.CANCELLED]: 'CANCEL',
  [DirectSalesStatusOption.DELETED]: 'DELETE',
  CONFIRM_OPTION: 'CONFIRM_OPTION',
};

export const LINE_LEVEL_VALID_CAMPAIGN_VERSIONING_COMMIT_STATES = [
  DirectSalesStatusOption.DRAFT_PRICED,
  DirectSalesStatusOption.OPTION,
  DirectSalesStatusOption.PROPOSAL,
  DirectSalesStatusOption.CONFIRMED,
];

export const VALID_CAMPAIGN_VERSIONING_COMMIT_STATES = [
  DirectSalesStatusOption.OPTION,
  DirectSalesStatusOption.PROPOSAL,
  DirectSalesStatusOption.CONFIRMED,
];

export enum DirectSalesLockStatusOption {
  LOCKED = 'LOCKED',
  UNLOCKED = 'UNLOCKED',
  UNSELECTED = 'UNSELECTED',
}

export const DirectSalesStatusLabels: Record<string, string> = {
  DRAFT_UPDATING: 'Update Draft',
  PROPOSAL: 'Proposal',
  OPTION: 'Option',
  CONFIRMED: 'Confirm',
  CANCELLED: 'Cancel',
  DELETED: 'Delete',
};

export type DirectSalesIntendedTargetState = {
  intendedDealLineState: DirectSalesStatusOption;
  expires?: Date | string;
};

export interface DirectSalesState {
  status: DirectSalesStatusOption;
  expires?: Date | string;
}

export interface DirectSalesLockState {
  status?: DirectSalesLockStatusOption;
  expires?: Date | string;
  owner?: string;
}

export interface DirectSalesWeeklyPatternEntry {
  day: Days;
  hours: number[];
}

export type DirectSalesNoRepeatPatternEntry = {
  date: string;
  hours: number[];
};

export type DirectSalesWeeklyPattern = {
  type: 'WEEKLY';
  entries: DirectSalesWeeklyPatternEntry[];
};

export type DirectSalesNoRepeatPattern = {
  type: 'NO_REPEAT';
  entries: DirectSalesNoRepeatPatternEntry[];
};

export type DirectSalesRangePattern = DirectSalesWeeklyPattern | DirectSalesNoRepeatPattern;

export type DirectSalesChannel = CodeName & {
  audienceCategoryGroupCode: string;
  dayPartGroupCode: string;
};

export enum DirectSalesDealLineFailureCode {
  LOCKED_FRAME_CANT_REALLOCATE = 'LOCKED_FRAME_CANT_REALLOCATE',
  NO_FRAMES_AVAILABLE = 'NO_FRAMES_AVAILABLE',
}

export const DirectSalesDealLineFailureMessage: Record<DirectSalesDealLineFailureCode, string> = {
  [DirectSalesDealLineFailureCode.LOCKED_FRAME_CANT_REALLOCATE]:
    'Locked allocation is no longer available. Unlock and reallocate your campaign.',
  [DirectSalesDealLineFailureCode.NO_FRAMES_AVAILABLE]: 'There are no frames available for your targeted criteria.',
};

export type DirectSalesDealLineAllocationFailure = {
  lineId: string;
  lineName: string;
  failureCode: DirectSalesDealLineFailureCode;
};

export type DirectSalesDealLineAllocationFailureResponse = {
  failureCode: DirectSalesDealLineFailureCode;
};

export type DirectSalesCampaignDealLineResponse = {
  id?: string;
  name: string;
  templateId?: string;
  templateName?: string;
  productLineReference?: number;
  status: DirectSalesStatusOption;
  state: DirectSalesState;
  targetState?: DirectSalesIntendedTargetState;
  lockState: DirectSalesLockState;
  range: {
    startDate: string;
    endDate: string;
    floatingDateRange: FloatingDateRange;
  };
  pricingMode: DirectSalesPricingMode;
  rangePattern?: DirectSalesRangePattern;
  targetCriteria: DirectSalesTargetCriteria[];
  creationTime?: string;
  periodType?: PeriodType;
  weekStartDate?: Days | null;
  sweep?: string;
  filter?: DirectSalesFilter;
  allocationFailures?: DirectSalesDealLineAllocationFailureResponse[];
};

export type DirectSalesTransformedDealLineRequest = Omit<
  DirectSalesCampaignDealLineResponse,
  'channelIds' | 'state' | 'lockState' | 'range' | 'status'
> & { range: DirectSalesCampaignDealLineRangeRequest };

export type DirectSalesUnitIncludedExcludedFilter = {
  code: string;
  filename: string | null;
};

export enum DirectSalesFilterTypes {
  AndFilter = 'AndFilter',
  OrFilter = 'OrFilter',
  NotFilter = 'NotFilter',
}

export type DirectSalesNestedFilter = {
  type: DirectSalesFilterTypes;
  filter: {
    type: DirectSalesFilterTypes;
    filters: (DirectSalesFilterItem | DirectSalesUnitListFilter | DirectSalesPOIFilter | DirectSalesCustomPOIFilter)[];
  };
};

export type DirectSalesUnitListFilter = {
  type: string;
  filename?: string | null;
  unitsIds: string[];
};

export type DirectSalesMediaFilterItem = {
  type: string;
  mediaType: DirectSalesMediaType;
};

export type DirectSalesFilterItem = {
  type: string;
  id: string;
  name: string;
  groupName?: string;
};

export interface DirectSalesPOIFilter
  extends Omit<DirectSalesFilterItem, 'name'>,
    Pick<POI, 'radius' | 'dataProvider'>,
    Partial<Pick<POI, 'hasChildren' | 'rootCategoryType' | 'parentCategory' | 'categoryPath' | 'tooltip'>> {
  name: string | null;
  code?: string | null;
}

export interface DirectSalesCustomPOIFilterValue {
  name: string;
  latitude: number;
  longitude: number;
  radius: number;
}

export interface DirectSalesCustomPOIFilter {
  type: 'CustomPoiFilter';
  name: string;
  values: DirectSalesCustomPOIFilterValue[];
}

export interface DirectSalesSegmentFilter extends Omit<DirectSalesFilterItem, 'type'> {
  type: 'SecondaryAudienceFilter';
  dataProvider?: string;
  cpmPremium?: number;
  cpmCurrency?: string;
  cpmPercentage?: number;
  minThreshold?: number;
  maxThreshold?: number;
}

export enum DirectSalesFilterGroupNames {
  UnitListFilter = 'UnitListFilter',
  MapSelectionListFilter = 'MapSelectionListFilter',
}

export type DirectSalesLogicFilterTypes =
  | DirectSalesFilterItem
  | DirectSalesNestedFilter
  | DirectSalesFilter
  | DirectSalesUnitListFilter
  | DirectSalesMediaFilterItem
  | DirectSalesPOIFilter
  | DirectSalesCustomPOIFilter
  | DirectSalesSegmentFilter;

export type DirectSalesFilter = {
  type: DirectSalesFilterTypes;
  filters: DirectSalesLogicFilterTypes[];
};

export type DirectSalesCampaignDealLineRequest = {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  floatingRange: FloatingDateRange;
  pricingMode: DirectSalesPricingMode;
  filter?: DirectSalesFilter;
  impressions?: number;
  budget?: number;
  frames?: number;
  budgetCriteria: DirectSalesTargetTolerance & {
    objectiveId?: number | null;
    priority: CodeNameModel;
  };
  framesCriteria: DirectSalesTargetTolerance & {
    objectiveId?: number | null;
    priority: CodeNameModel;
  };
  impressionsCriteria: DirectSalesTargetTolerance & {
    objectiveId?: number | null;
    priority: CodeNameModel;
  };
  objectives?: DirectSalesLineObjective[];
  networkCriteria: NetworkCriteriaType[];
  sot?: number;
  sotObjectiveId?: number | null;
  sweep?: string;
  mediaType?: DirectSalesMediaType;
  selectedDays?: GroupOfSelectedDays;
  patternLength?: number;
  patternRepeatType?: PatternOption;
  productTemplate?: ProductTemplate;
  productLineReference?: number;
};

export type DirectSalesCampaignMultiDealLineRequest = {
  dealLines: DirectSalesTransformedDealLineRequest[];
};

export type DirectSalesTransientDealLineState = {
  [key: string]: DirectSalesTransientStatus;
};

export type DirectSalesTransientStatus = {
  status: DirectSalesStatusOption;
  expires?: string | Date;
};

export enum DirectSalesTransientStates {
  ACCEPT = 'ACCEPT',
  CANCEL = 'CANCEL',
}

export type DirectSalesUpdateTransientDealStateRequest = {
  transientAction: string;
};

export enum DirectSalesDealLineLockStateEvent {
  LOCKING = 'LOCKING',
  UNLOCKING = 'UNLOCKING',
}

export type DirectSalesFilteredCampaignsRequestResponse = FilteredCampaignsRequestResponse<
  DirectSalesFilteredCampaignsTableResponse[]
>;

export type DirectSalesFilteredCampaignsStatusTableResponse =
  | 'DRAFT'
  | keyof Pick<
      typeof DirectSalesStatusOption,
      'PROPOSAL' | 'CONFIRMED' | 'OPTION' | 'CANCELLED' | 'MIXED' | 'LIVE' | 'ENDED'
    >;

export interface DirectSalesFilteredCampaignsTableResponse {
  id: string;
  reference: string;
  userVisibleId?: string | null;
  campaignName: string;
  campaignStatus: DirectSalesFilteredCampaignsStatusTableResponse | null;
  publisher: string;
  dealLinesNames: string[];
  startDate: string;
  endDate: string;
  advertiser: string;
  brand: string;
  productCategory: string;
  specialist: string | null;
  agency: string | null;
  campaignType: string;
  channels: string[];
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
  dropDate: string | null;
  salesTeam: string | null;
  adminPerson: string | null;
  salesPerson: string | null;
  exclusions: {
    organisations: string[];
    indexedProductCategories: string[];
  } | null;
  dealLineStates: DirectSalesStatusOption[];
  campaignMediaTypes: string[];
}

export type SearchFieldOption =
  | 'ID'
  | 'USER_VISIBLE_ID'
  | 'CAMPAIGN_LOOKUP_KEY'
  | 'CAMPAIGN_NAME'
  | 'DEAL_LINE_STATES'
  | 'CHANNELS'
  | 'PUBLISHER'
  | 'START_DATE'
  | 'END_DATE'
  | 'ADVERTISER'
  | 'BRAND'
  | 'PRODUCT_CATEGORY'
  | 'SPECIALIST'
  | 'AGENCY'
  | 'CAMPAIGN_TYPE'
  | 'DEAL_LINE_NAMES'
  | 'CREATED_BY'
  | 'CREATED_AT'
  | 'UPDATED_BY'
  | 'UPDATED_AT'
  | 'DROP_DATE'
  | 'SALES_TEAM'
  | 'ADMIN_PERSON'
  | 'SALES_PERSON'
  | 'EXCLUDED_ORGANISATIONS'
  | 'EXCLUDED_PRODUCT_CATEGORIES'
  | 'CAMPAIGN_STATUS'
  | 'CAMPAIGN_MEDIA_TYPE';

type SearchOperationOption =
  | 'EQUAL'
  | 'LESS_THAN'
  | 'GREATER_THAN'
  | 'LESS_OR_EQUAL'
  | 'GREATER_OR_EQUAL'
  | 'CONTAINS'
  | 'IN';

export interface GetFiltersRequestData {
  search: {
    field: SearchFieldOption;
    operation: SearchOperationOption;
    value: Date | string | string[];
  }[];
  sortBy?: SearchFieldOption;
  sortDir?: string;
}

export type SearchConfigSortDirection = 'ASC' | 'DESC';

export interface SearchConfigColumn {
  key: string;
  visible: boolean;
  sort: SearchConfigSortDirection | null;
}

export interface SearchConfigFilter {
  field: SearchFieldOption;
  operation: Exclude<SearchOperationOption, 'IN'>;
  value: string | string[];
}

export interface SearchConfig {
  columns: SearchConfigColumn[];
  filters: SearchConfigFilter[];
}

export type DirectSalesGrid = CampaignGridLine[];

export type DirectSalesUpdatedCampaignNegotiationType = {
  rowKey: string;
  negotiationType: string;
  originalNegotiationType?: string;
};

export type DirectSalesUpdatedCampaignNegotiationTypes = DirectSalesUpdatedCampaignNegotiationType[];

export interface CampaignGridLine {
  discountLineKey: string;
  discountValues: CampaignGridLineRowCell[];
}

export interface DiscountLineGridAttributes {
  NEGOTIATION_TYPE: string;
}

export type DirectSalesUpdateNegotiationTypesRequest = {
  discountLineKeyGridAttributes: Record<string, DiscountLineGridAttributes>;
};

export interface CampaignGridLineRowCell {
  discountKey: string;
  discountValue: string | null;
  discountValueType: string;
}

export type DirectSalesCampaignLevelFields = {
  [fieldKey: string]: Advertiser | Brand | CodeNameModel | null | undefined;
};

export type DirectSalesSplitLineRequest = {
  splitPoint: string;
  dealLineName: string;
};

export type DirectSalesDuplicateLineRequest = {
  dealLineName: string;
};

export type TransformedDirectSalesBookingResponse = {
  deal: Partial<CommonDeal>;
  lines: Partial<DirectSalesLine>[];
  sessionLockState: DirectSalesLockState;
};

export type DirectSalesCampaignWithLineFormData = {
  commonDeal: Partial<CommonDeal>;
  directSales: Partial<DirectSales>;
  currentLine: Partial<Line>;
};

export interface OOCFrameOccupancy {
  frameCount: number;
  occupancyWeek: {
    week: number;
    weekStartDate: string;
  };
}

export interface OOCFrameDetails {
  frameId: string;
  businessArea: string;
  routeFrameCode: string;
  sizeCode: string;
  furnitureId: string;
  productFormat: string;
  mediaType: string;
  routeTown: string;
  address: string;
}

export interface OOCReportCampaignDetails {
  campaignId: string;
  userVisibleId?: string | null;
  salesTeam: string;
  salesPerson: string;
  adminPerson: string;
  brand: string;
  status: DirectSalesStatusOption;
  campaignStartDate: string;
  campaignEndDate: string;
  frameOccupancies: OOCFrameOccupancy[];
  frameDetails: OOCFrameDetails[];
}

export interface OOCReport {
  campaignDetails: OOCReportCampaignDetails[];
}

/* eslint-enable import/no-cycle */
